import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { moderateScale } from "@utils/scale";
import { memo } from "react";
import {
  GestureResponderEvent,
  ActivityIndicator,
  Platform,
} from "react-native";
import SkeletonPlaceholder from "react-native-skeleton-placeholder";
import OptionsButton from "@components/SongOptions/OptionsButton";
import { theme } from "@theme";
import { useIsPrivateSongUnlocked } from "@hooks/useIsPrivateSongUnlocked";
import type { CatalogCollectionType, CatalogSong } from "@utils/player";
import { useTrackOfflineStatus } from "@utils/downloadManager/hooks";
import { useActiveSongId } from "@utils/player";
import { router } from "expo-router";
import { toast } from "sonner-native";
import { prefetchAccessDetailHeroImage } from "@utils/accesses/access";
import { ExclusiveRibbon } from "@libs/shared-ui/native";

interface SongProps {
  catalogSong?: CatalogSong;
  collectionId: string;
  collectionType: CatalogCollectionType;
  disabled?: boolean;
  enableOptions?: boolean;
  enablePlaybackStatus?: boolean;
  fromName?: string;
  index?: number;
  onLongPress?: ((event: GestureResponderEvent) => void) | undefined;
  onPress?: (catalogSong: CatalogSong) => void;
  rightElement?: React.ReactNode;
  select?: (id: string) => void;
  selected?: boolean;
  showArtwork?: boolean;
  showSelect?: boolean;
  textAlign?: "left" | "center" | "right";
  trackNumber?: number;
}

const Title = ({
  enablePlaybackStatus,
  collectionId,
  song,
  textAlign,
  isLocked,
}: {
  enablePlaybackStatus?: boolean;
  collectionId: string;
  song: {
    artists: {
      id: string;
      name: string;
      image: string | null;
      role: string;
    }[];
    id: string;
    title: string;
    private: boolean;
    advisory?: string | null;
  };
  textAlign?: "left" | "center" | "right";
  isLocked: boolean;
}) => {
  const activeSongId = useActiveSongId();
  const status = useTrackOfflineStatus(collectionId, song.id);
  const isPlaying = activeSongId === song?.id;

  let songTitleTextColor: keyof typeof theme.colors = "textPrimary";
  let artistTextColor: keyof typeof theme.colors = "soundArtistText";
  if (enablePlaybackStatus && isPlaying) {
    songTitleTextColor = "soundPlayingText";
    artistTextColor = "soundPlayingText";
  }
  if (isLocked) {
    songTitleTextColor = "soundLockedText";
    artistTextColor = "soundLockedText";
  }

  return (
    <LBox
      flexGrow={Platform.OS !== "web" ? 1 : 0}
      flexShrink={Platform.OS === "web" ? 1 : 0}
    >
      <LBox flexDirection="row">
        <LText
          color={songTitleTextColor}
          variant="Body/B1/Medium"
          numberOfLines={1}
          textAlign={textAlign}
          mb="s1"
          style={{ flex: 1, flexShrink: 1 }}
        >
          {song.title}
        </LText>
      </LBox>
      {/* This LBox is required for text shrink to work */}
      <LBox flexDirection="row" gap="s1" alignItems="center">
        {status === "downloading" ? (
          <ActivityIndicator
            size="small"
            style={[
              { transform: [{ scaleX: 0.5 }, { scaleY: 0.5 }] },
              { width: 12, height: 12 },
            ]}
          />
        ) : null}
        {status === "downloaded" ? <LIcon name="downloaded" size={12} /> : null}
        {song.advisory === "Explicit" ? (
          <LBox
            backgroundColor="subTextGrey"
            borderRadius="xs"
            height={12}
            width={12}
          >
            <LText
              variant="bodyLBold"
              fontSize={8}
              lineHeight={12}
              fontWeight="bold"
              color="black"
              textAlign="center"
            >
              E
            </LText>
          </LBox>
        ) : null}
        <LText
          color={artistTextColor}
          variant="Body/B2/Medium"
          numberOfLines={1}
          textAlign={textAlign}
          style={
            song.private && !isLocked
              ? { flexShrink: 1 }
              : { flex: 1, flexShrink: 1 }
          }
        >
          {[...new Set(song.artists.map((artist) => artist.name))].join(", ") ??
            ""}
        </LText>
        {song.private && !isLocked ? (
          <LBox
            borderWidth={1}
            borderColor="exclusiveGold"
            borderRadius="l"
            px="s1"
            flexShrink={0}
          >
            <LText
              color="exclusiveGold"
              fontSize={8}
              lineHeight={12}
              fontWeight="800"
              style={{ letterSpacing: 0.4 }}
            >
              UNLOCKED
            </LText>
          </LBox>
        ) : null}
      </LBox>
    </LBox>
  );
};

const EmptySong = () => {
  return (
    <LBox>
      <SkeletonPlaceholder borderRadius={4} angle={20}>
        <SkeletonPlaceholder.Item flexDirection="row" alignItems="center">
          <SkeletonPlaceholder.Item width={43} height={43} marginRight={16} />
          <SkeletonPlaceholder.Item flex={1} paddingVertical={3.4}>
            <SkeletonPlaceholder.Item
              flex={1}
              height={14}
              borderRadius={20}
              marginBottom={10}
            />
            <SkeletonPlaceholder.Item
              flex={1}
              width={23}
              height={12}
              borderRadius={20}
            />
          </SkeletonPlaceholder.Item>
        </SkeletonPlaceholder.Item>
      </SkeletonPlaceholder>
    </LBox>
  );
};

const SongArtwork = ({ catalogSong }: { catalogSong: CatalogSong }) => {
  const artworkSize = moderateScale(48);
  // Locked and unlocked exclusives share the plain artwork — the VIP ribbon
  // below marks exclusivity (the old green stroke is gone, LIS-602).
  const imageUrl = catalogSong.albums.at(0)?.image;
  const artwork = (
    <LArtwork
      // A recycled row keeps its last image when the next song has none, so
      // remount on that switch to show the placeholder. Keying on the URL
      // itself would remount the image on every reuse and undo the recycling.
      key={imageUrl ? "image" : "placeholder"}
      url={imageUrl}
      size={artworkSize}
      imageSize="small"
      borderRadius="m"
      showTracklistChip={catalogSong.isSetList}
    />
  );

  return (
    <LBox
      width={artworkSize}
      height={artworkSize}
      borderRadius="m"
      overflow="hidden"
    >
      {artwork}
      {catalogSong.private ? (
        <ExclusiveRibbon containerSize={artworkSize} variant="compact" />
      ) : null}
    </LBox>
  );
};

const SongRightSide = ({
  catalogSong,
  rightElement,
  collectionId,
  collectionType,
  enableOptions,
  isLocked,
  showTracklistInlineChip,
}: {
  catalogSong: CatalogSong;
  rightElement: React.ReactNode;
  collectionId: string;
  collectionType: CatalogCollectionType;
  enableOptions: boolean;
  isLocked: boolean;
  showTracklistInlineChip: boolean;
}) => {
  if (isLocked) {
    return (
      <LText color="exclusiveGold" variant="Body/B1/SemiBold">
        Unlock
      </LText>
    );
  }

  if (rightElement) {
    return (
      <LBox flexDirection="row" alignItems="center" gap="s2">
        {showTracklistInlineChip ? <TracklistInlineChip /> : null}
        {rightElement}
      </LBox>
    );
  }
  if (enableOptions) {
    return (
      <LBox pr="s0_5" flexDirection="row" alignItems="center">
        {showTracklistInlineChip ? <TracklistInlineChip /> : null}
        <OptionsButton
          selectedCollection={{
            collectionId,
            collectionType,
          }}
          selectedCatalogSong={catalogSong}
          sentryEventName="song_options"
        />
      </LBox>
    );
  }
  return null;
};

const TracklistInlineChip = () => (
  <LBox
    borderRadius="s"
    borderWidth={1}
    mr="s3"
    px="s1_5"
    py="s0_5"
    style={{ borderColor: "rgba(255, 255, 255, 0.9)" }}
  >
    <LText
      color="white"
      variant="Caption/C2/Medium"
      style={{ letterSpacing: 0 }}
    >
      DJ SET
    </LText>
  </LBox>
);

const LockedSongButton = ({
  children,
  isLocked,
  song,
}: {
  children: React.ReactNode;
  isLocked: boolean;
  song: CatalogSong;
}) => {
  const isExternal = song.status === "external";

  const showToastMessage = () => {
    toast.success(
      song.unavailable
        ? "This song is currently unavailable"
        : isExternal
          ? "Coming soon on Lissen!"
          : "This song isn't available in your country",
      { duration: 1500 },
    );
  };

  if (isExternal) {
    return (
      <LPressable
        onPress={showToastMessage}
        sentryEventName="song_external_toast_message"
      >
        {children}
      </LPressable>
    );
  }
  if (isLocked && song.accessId) {
    return (
      <LPressable
        onPress={() => {
          prefetchAccessDetailHeroImage(song.albums.at(0)?.image);
          router.push(`/access/${song.accessId}`);
        }}
        sentryEventName="Song"
      >
        {children}
      </LPressable>
    );
  }
  return <>{children}</>;
};

const Song = ({
  catalogSong,
  textAlign,
  enablePlaybackStatus = true,
  enableOptions = true,
  disabled,
  showSelect,
  selected,
  select,
  rightElement,
  collectionType,
  collectionId,
  onLongPress,
  onPress,
  showArtwork,
  trackNumber,
}: SongProps) => {
  const isUnlocked = useIsPrivateSongUnlocked(catalogSong?.id);

  if (!catalogSong) {
    return <EmptySong />;
  }
  const isLocked = catalogSong.private && !isUnlocked;
  const isUnavailable = catalogSong.status === "external";

  const { id } = catalogSong;

  const songRightElement = isUnavailable ? (
    <LIcon name="disabled" size={20} />
  ) : (
    rightElement
  );
  const showTracklistInlineChip =
    catalogSong.isSetList && collectionType === "album" && !showArtwork;

  return (
    <LockedSongButton isLocked={isLocked} song={catalogSong}>
      <LBox
        alignItems="center"
        flexDirection="row"
        opacity={isUnavailable ? 0.5 : 1}
      >
        {showSelect ? (
          <LPressable
            sentryEventName={`song_select_${id}`}
            disabled={isLocked || isUnavailable}
            onPress={() => select?.(id)}
          >
            <LBox alignSelf="center" marginRight="s4">
              {selected ? (
                <LIcon
                  colors={{ fill: "white", check: "slate" }}
                  name="selected"
                  size={24}
                />
              ) : (
                <LIcon
                  colors={{ color: "buttonGrey" }}
                  name="select"
                  size={24}
                />
              )}
            </LBox>
          </LPressable>
        ) : null}
        <LBox
          flex={1}
          flexDirection={"row"}
          alignItems="center"
          justifyContent="space-between"
        >
          <LPressable
            sentryEventName={`song_${id}`}
            onPress={() => {
              onPress?.(catalogSong);
            }}
            delayLongPress={100}
            onLongPress={onLongPress}
            disabled={isLocked || disabled || isUnavailable}
            style={{
              opacity: disabled ? 0.5 : 1,
              flex: 1,
            }}
          >
            <LBox flexDirection="row" gap="s4" alignItems="center">
              {trackNumber ? (
                <LText variant="bodyM">{trackNumber}</LText>
              ) : null}
              {showArtwork ? <SongArtwork catalogSong={catalogSong} /> : null}
              <Title
                collectionId={collectionId}
                song={catalogSong}
                textAlign={textAlign}
                enablePlaybackStatus={enablePlaybackStatus}
                isLocked={isLocked}
              />
            </LBox>
          </LPressable>
        </LBox>
        <SongRightSide
          catalogSong={catalogSong}
          rightElement={songRightElement}
          collectionId={collectionId}
          collectionType={collectionType}
          enableOptions={enableOptions}
          isLocked={isLocked}
          showTracklistInlineChip={showTracklistInlineChip}
        />
      </LBox>
    </LockedSongButton>
  );
};

export default memo(Song);
